import {
  FreeEdgeLabelModel,
  type IEdge,
  type IGraph,
  type ILabelModelParameter,
  type ILabelOwner,
  INode,
  Insets,
  InteriorNodeLabelModel,
  InteriorNodeLabelModelPosition,
  SmartEdgeLabelModel,
} from '@ardoq/yfiles';
import { ArdoqURLLabelStyle } from 'yfilesExtensions/styles/ardoqURLLabelStyle';
import { UrlLabelStyle } from 'yfilesExtensions/styles/UrlLabelStyle';
import {
  type AggregatedGraphEdge,
  type ComponentGraphGroup,
  type GraphItem,
  type GraphEdgeBase,
  ComponentGraphAggregatedEdgeModel,
  ComponentGraphNode,
} from '@ardoq/graph';
import { DescendantCountLabelStyle } from '@ardoq/graph/yfiles';
import {
  MODERNIZED_BLOCK_DIAGRAM_GROUP_PADDING as GROUP_PADDING,
  MODERNIZED_BLOCK_DIAGRAM_COLLAPSED_COMPONENT_GROUP_STRIPE_HEIGHT as COLLAPSED_COMPONENT_GROUP_STRIPE_HEIGHT,
  INVISIBLE_SEPARATOR,
} from '../consts';
import { MODERNIZED_BLOCK_DIAGRAM_URL_ICON_SIZE } from 'yfilesExtensions/styles/modernized/consts';
import { OpenGroupUrlButtonLabelModel } from '../groupLabels/OpenGroupUrlLabelModel';
import { getLabelOwnerUrlFieldValues } from 'yfilesExtensions/styles/util';

const URL_NODE_BUTTON_MARGIN = 8;

/**
 * the layout parameter used for the URL Icon Button on nodes.
 * the URL Icon Button is displayed over components containing URL fields.
 *
 * a label with a font icon is used for the URL Icon Button. this parameter describes the placement of a label relative to the node bounds.
 */
const urlNodeButtonModelParameter = new InteriorNodeLabelModel({
  padding: new Insets(URL_NODE_BUTTON_MARGIN, URL_NODE_BUTTON_MARGIN, 0, 0),
}).createParameter(InteriorNodeLabelModelPosition.TOP_RIGHT);
const urlCollapsedComponentGroupNodeButtonModelParameter =
  new InteriorNodeLabelModel({
    padding: new Insets(
      COLLAPSED_COMPONENT_GROUP_STRIPE_HEIGHT +
        GROUP_PADDING +
        MODERNIZED_BLOCK_DIAGRAM_URL_ICON_SIZE / 2,
      GROUP_PADDING,
      0,
      0
    ),
  }).createParameter(InteriorNodeLabelModelPosition.TOP_RIGHT);
const urlEdgeButtonModelParameter =
  FreeEdgeLabelModel.INSTANCE.createParameter();

const aggregatedEdgeCountLabelParameter = new SmartEdgeLabelModel({
  autoRotation: false,
}).createParameterFromCenter();

const isCollapsedComponentGroup = (group: ComponentGraphGroup) =>
  group.collapsed && group.isComponent();
const addURLLabel = (
  graph: IGraph,
  owner: ILabelOwner,
  graphNode: GraphItem,
  buttonModelParameter: ILabelModelParameter,
  isModern: boolean
) =>
  graph.addLabel({
    owner,
    text:
      getLabelOwnerUrlFieldValues(owner)
        ?.map(({ url }) => url)
        .join(INVISIBLE_SEPARATOR) ?? '',
    layoutParameter: buttonModelParameter,
    style: isModern ? UrlLabelStyle.Instance : ArdoqURLLabelStyle.Instance,
    tag: graphNode,
  });
export const addURLEdgeLabel = (
  graph: IGraph,
  owner: IEdge,
  graphEdge: GraphEdgeBase,
  isModern: boolean
) =>
  addURLLabel(graph, owner, graphEdge, urlEdgeButtonModelParameter, isModern);

export const addURLNodeLabel = (
  graph: IGraph,
  owner: INode,
  graphNode: ComponentGraphNode
) => addURLLabel(graph, owner, graphNode, urlNodeButtonModelParameter, true);

export const addGroupURLNodeLabel = (
  graph: IGraph,
  owner: INode,
  graphNode: ComponentGraphGroup
) =>
  addURLLabel(
    graph,
    owner,
    graphNode,
    isCollapsedComponentGroup(graphNode)
      ? urlCollapsedComponentGroupNodeButtonModelParameter
      : new OpenGroupUrlButtonLabelModel(graph).createParameter(),
    true
  );
export const addAggregatedCountLabel = (
  graph: IGraph,
  owner: IEdge,
  graphEdge: AggregatedGraphEdge | ComponentGraphAggregatedEdgeModel
) =>
  graph.addLabel({
    owner,
    text: `${graphEdge.modelIds.length}`,
    style: DescendantCountLabelStyle.Instance,
    layoutParameter: aggregatedEdgeCountLabelParameter,
    tag: graphEdge,
  });
